import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMaintenance } from '../../contexts/MaintenanceContext';
import { progressApi } from '../../api/progress';
import { readOfflineTasbeeh, pendingTasbeeh, acknowledgeOfflineTasbeeh, OFFLINE_TASBEEH_CHANGED, OFFLINE_TASBEEH_SYNCED } from '../../services/offlineTasbeehStorage';

export default function OfflineTasbeehSync() {
  const { currentUserId, loading } = useAuth();
  const { blocked, checking, online, error } = useMaintenance();
  useEffect(() => {
    if (!currentUserId || loading || checking || error || !online || blocked) return undefined;
    let stopped = false, running = false, timer, retryDelay = 15000;
    const controller = new AbortController();
    const schedule = (delay = 1500) => { clearTimeout(timer); if (!stopped) timer = setTimeout(sync, delay); };
    async function sync() {
      if (running || stopped || navigator.onLine === false) return;
      running = true;
      try {
        const snapshot = pendingTasbeeh(await readOfflineTasbeeh(currentUserId));
        if (!snapshot || stopped) return;
        const response = await progressApi.syncOfflineTasbeeh(snapshot, controller.signal);
        const totals = response.totals || response;
        // The server also checks accountId, including across automatic token refreshes.
        await acknowledgeOfflineTasbeeh(response.progress || !snapshot.dailyCounts ? snapshot : { ...snapshot, dailyCounts: [] });
        if (stopped) return;
        window.dispatchEvent(new CustomEvent(OFFLINE_TASBEEH_SYNCED, { detail: { accountId: currentUserId, totals, ...(response.progress ? { progress: response.progress } : {}), ...(response.dhikrDailyCounts ? {dhikrDailyCounts:response.dhikrDailyCounts} : {}) } }));
        // During a rolling deployment an old server can acknowledge lifetime only.
        // Retain the dated ledger and back off until the server supports daily goals.
        if (snapshot.dailyCounts?.length && !response.progress) throw new Error('Daily progress acknowledgement unavailable');
        retryDelay = 15000;
        schedule(); // A tap made while this request was in flight remains pending.
      } catch {
        if (!stopped) { schedule(retryDelay); retryDelay = Math.min(retryDelay * 2, 300000); }
      } finally { running = false; }
    }
    const changed = () => schedule();
    const visible = () => { if (document.visibilityState === 'visible') schedule(0); };
    void sync();
    window.addEventListener(OFFLINE_TASBEEH_CHANGED, changed);
    document.addEventListener('visibilitychange', visible);
    return () => {
      stopped = true; clearTimeout(timer); controller.abort();
      window.removeEventListener(OFFLINE_TASBEEH_CHANGED, changed);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [currentUserId, loading, blocked, checking, online, error]);
  return null;
}
